import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import type { PaginatedVpnKeys } from '../services/types';
import { formatDate } from '../utils/format';

export function VpnPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<PaginatedVpnKeys | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const page = Number(searchParams.get('page') || '1');
  const q = searchParams.get('q') || '';
  const server_code = searchParams.get('server_code') || '';
  const status = searchParams.get('status') || 'active';
  const user_id = searchParams.get('user_id') || '';

  const [qInput, setQInput] = useState(q);
  const [userIdInput, setUserIdInput] = useState(user_id);
  const [serverInput, setServerInput] = useState(server_code);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.listVpnKeys({
      page,
      page_size: 20,
      q: q || undefined,
      server_code: server_code || undefined,
      status: status !== 'all' ? status : 'all',
      user_id: user_id && /^\d+$/.test(user_id) ? Number(user_id) : undefined,
    });
    if (res.data) setData(res.data);
    else if (res.error) setError(res.error.message);
    setLoading(false);
  }, [page, q, server_code, status, user_id]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (qInput) next.set('q', qInput);
    else next.delete('q');
    if (userIdInput) next.set('user_id', userIdInput);
    else next.delete('user_id');
    if (serverInput) next.set('server_code', serverInput);
    else next.delete('server_code');
    next.set('page', '1');
    setSearchParams(next);
  };

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setSearchParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <h1 style={{ marginTop: 0 }}>VPN ключи</h1>
        <Link to="/servers" className="btn btn-ghost">
          Серверы →
        </Link>
      </div>

      <form
        onSubmit={applyFilters}
        style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}
      >
        <input
          className="input"
          placeholder="Поиск: ID, client_id, сервер…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          style={{ minWidth: 200 }}
        />
        <input
          className="input"
          placeholder="User ID"
          value={userIdInput}
          onChange={(e) => setUserIdInput(e.target.value)}
          style={{ width: 120 }}
        />
        <input
          className="input"
          placeholder="Сервер (ru, de…)"
          value={serverInput}
          onChange={(e) => setServerInput(e.target.value)}
          style={{ width: 120 }}
        />
        <select className="select" value={status} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="active">Активные</option>
          <option value="deleted">Удалённые</option>
          <option value="all">Все</option>
        </select>
        <button type="submit" className="btn btn-primary">
          Найти
        </button>
      </form>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}

      {!loading && data && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Пользователь</th>
                  <th>Сервер</th>
                  <th>Client ID</th>
                  <th>Статус</th>
                  <th>Создан</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ color: 'var(--text-muted)' }}>
                      Ключи не найдены
                    </td>
                  </tr>
                )}
                {data.items.map((key) => (
                  <tr key={key.id}>
                    <td>
                      <Link to={`/vpn/${key.id}`}>{key.id}</Link>
                    </td>
                    <td>
                      <Link to={`/users/${key.user_id}`}>
                        {key.first_name || key.username || key.user_id}
                      </Link>
                    </td>
                    <td>
                      <code>{key.server_country}</code>
                    </td>
                    <td>
                      <code style={{ fontSize: '0.75rem' }}>{key.client_id.slice(0, 12)}…</code>
                    </td>
                    <td>
                      <span className={key.status === 'active' ? 'badge badge-success' : 'badge'}>
                        {key.status}
                      </span>
                    </td>
                    <td>{formatDate(key.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.pages > 1 && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                ←
              </button>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                {page} / {data.pages} ({data.total} всего)
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= data.pages}
                onClick={() => setPage(page + 1)}
              >
                →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
